import React, { useState } from 'react';
import { CreditCard, AlertCircle, CheckCircle, Loader } from 'lucide-react';
import UserSearch from './UserSearch';
import { useAuthenticatedApi } from '../../hooks/useAuthenticatedApi';

interface UserInfo {
  user_id: string;
  email: string;
  email_verified: boolean;
  name?: string;
  phone_number?: string;
  created_at?: string;
  updated_at?: string;
}

const creditGrantTypes = [
  { value: 'signup_bonus', label: 'Signup Bonus' },
  { value: 'promotional', label: 'Promotional' },
  { value: 'referral_bonus', label: 'Referral Bonus' },
  { value: 'purchased', label: 'Purchased' },
  { value: 'support_credit', label: 'Support Credit' },
  { value: 'milestone_reward', label: 'Milestone Reward' },
  { value: 'compensation', label: 'Compensation' },
  { value: 'trial', label: 'Trial' },
];

const GrantCreditsSection: React.FC = () => {
  const authenticatedApi = useAuthenticatedApi();
  const [selectedUser, setSelectedUser] = useState<UserInfo | null>(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);
  
  // Form fields
  const [amount, setAmount] = useState('');
  const [grantType, setGrantType] = useState('support_credit');
  const [description, setDescription] = useState('');
  const [expiresInDays, setExpiresInDays] = useState('');
  const [referenceId, setReferenceId] = useState('');

  const handleUserSelect = (userInfo: UserInfo) => {
    setSelectedUser(userInfo);
    setError(null);
    setSuccess(null);
  };

  const resetForm = () => {
    setAmount('');
    setGrantType('support_credit');
    setDescription('');
    setExpiresInDays('');
    setReferenceId('');
    setSelectedUser(null);
    // Don't clear success message here
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!selectedUser) {
      setError('Please select a user first');
      return;
    }

    if (!amount || parseFloat(amount) <= 0) {
      setError('Please enter a valid amount greater than 0');
      return;
    }

    if (!description.trim()) {
      setError('Please provide a description');
      return;
    }

    setIsSubmitting(true);
    setError(null);
    setSuccess(null);

    try {
      const payload: any = {
        user_id: selectedUser.user_id,
        amount: parseFloat(amount),
        grant_type: grantType,
        description: description.trim(),
        metadata: {},
      };

      if (expiresInDays) {
        payload.expires_in_days = parseInt(expiresInDays);
      }

      if (referenceId.trim()) {
        payload.reference_id = referenceId.trim();
      }

      const result = await authenticatedApi.post('/billing/grant', payload);
      
      setSuccess(`Successfully granted $${amount} to ${selectedUser.email}`);
      resetForm();
      // Clear success message after a delay
      setTimeout(() => setSuccess(null), 5000);
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Failed to grant credits';
      setError(errorMessage);
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="p-6">
      <h2 className="text-2xl font-semibold text-neutral-900 dark:text-white mb-6">
        Grant Credits
      </h2>

      <div className="space-y-6">
        {/* User Selection */}
        {!selectedUser ? (
          <div>
            <h3 className="text-lg font-medium text-neutral-900 dark:text-white mb-3">
              Select User
            </h3>
            <UserSearch
              onUserSelect={handleUserSelect}
              disabled={isSubmitting}
              label="User ID or Email"
              placeholder="Enter user ID or email to grant credits"
              buttonText="Select User for Credits"
              helpText={
                <>
                  <p>Search for a user by <strong>user ID</strong> or <strong>email address</strong> to grant them credits.</p>
                  <p>• Email search is case-insensitive</p>
                  <p>• User must exist in the system database</p>
                </>
              }
            />
          </div>
        ) : (
          <div>
            <h3 className="text-lg font-medium text-neutral-900 dark:text-white mb-3">
              Selected User
            </h3>
            <div className="p-3 bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800 rounded-lg">
              <div className="flex justify-between items-start">
                <div>
                  <p className="text-sm text-blue-800 dark:text-blue-200">
                    <span className="font-medium">{selectedUser.email}</span>
                  </p>
                  <p className="text-xs text-blue-700 dark:text-blue-300 mt-1">
                    User ID: {selectedUser.user_id}
                  </p>
                </div>
                <button
                  onClick={() => setSelectedUser(null)}
                  className="text-xs text-blue-600 dark:text-blue-400 hover:text-blue-800 dark:hover:text-blue-200"
                >
                  Change user
                </button>
              </div>
            </div>
          </div>
        )}

        {/* Grant Form */}
        {selectedUser && (
          <form onSubmit={handleSubmit} className="space-y-4">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              {/* Amount */}
              <div>
                <label htmlFor="amount" className="block text-sm font-medium text-neutral-700 dark:text-neutral-300 mb-1">
                  Amount (USD) *
                </label>
                <div className="relative">
                  <span className="absolute left-3 top-1/2 -translate-y-1/2 text-neutral-500 dark:text-neutral-400">$</span>
                  <input
                    type="number"
                    id="amount"
                    step="0.01"
                    min="0.01"
                    value={amount}
                    onChange={(e) => setAmount(e.target.value)}
                    className="w-full pl-8 pr-3 py-2 border border-neutral-300 dark:border-neutral-600 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 dark:bg-neutral-700 dark:text-white"
                    placeholder="0.00"
                    disabled={isSubmitting}
                    required
                  />
                </div>
              </div>

              {/* Grant Type */}
              <div>
                <label htmlFor="grantType" className="block text-sm font-medium text-neutral-700 dark:text-neutral-300 mb-1">
                  Grant Type *
                </label>
                <select
                  id="grantType"
                  value={grantType}
                  onChange={(e) => setGrantType(e.target.value)}
                  className="w-full px-3 py-2 border border-neutral-300 dark:border-neutral-600 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 dark:bg-neutral-700 dark:text-white"
                  disabled={isSubmitting}
                  required
                >
                  {creditGrantTypes.map((type) => (
                    <option key={type.value} value={type.value}>
                      {type.label}
                    </option>
                  ))}
                </select>
              </div>
            </div>

            {/* Description */}
            <div>
              <label htmlFor="description" className="block text-sm font-medium text-neutral-700 dark:text-neutral-300 mb-1">
                Description *
              </label>
              <textarea
                id="description"
                value={description}
                onChange={(e) => setDescription(e.target.value)}
                className="w-full px-3 py-2 border border-neutral-300 dark:border-neutral-600 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 dark:bg-neutral-700 dark:text-white"
                rows={3}
                placeholder="Reason for granting credits..."
                disabled={isSubmitting}
                required
              />
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              {/* Expiration */}
              <div>
                <label htmlFor="expiresInDays" className="block text-sm font-medium text-neutral-700 dark:text-neutral-300 mb-1">
                  Expires In (Days)
                </label>
                <input
                  type="number"
                  id="expiresInDays"
                  min="1"
                  value={expiresInDays}
                  onChange={(e) => setExpiresInDays(e.target.value)}
                  className="w-full px-3 py-2 border border-neutral-300 dark:border-neutral-600 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 dark:bg-neutral-700 dark:text-white"
                  placeholder="Leave empty for no expiration"
                  disabled={isSubmitting}
                />
              </div>

              {/* Reference ID */}
              <div>
                <label htmlFor="referenceId" className="block text-sm font-medium text-neutral-700 dark:text-neutral-300 mb-1">
                  Reference ID
                </label>
                <input
                  type="text"
                  id="referenceId"
                  value={referenceId}
                  onChange={(e) => setReferenceId(e.target.value)}
                  className="w-full px-3 py-2 border border-neutral-300 dark:border-neutral-600 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 dark:bg-neutral-700 dark:text-white"
                  placeholder="External reference (optional)"
                  disabled={isSubmitting}
                />
              </div>
            </div>

            {/* Submit Button */}
            <div className="flex justify-end pt-2">
              <button
                type="submit"
                disabled={isSubmitting}
                className="inline-flex items-center px-4 py-2 bg-blue-600 hover:bg-blue-700 disabled:bg-blue-400 text-white font-medium rounded-lg transition-colors disabled:cursor-not-allowed"
              >
                {isSubmitting ? (
                  <>
                    <Loader size={16} className="animate-spin mr-2" />
                    Granting Credits...
                  </>
                ) : (
                  <>
                    <CreditCard size={16} className="mr-2" />
                    Grant Credits
                  </>
                )}
              </button>
            </div>
          </form>
        )}

        {/* Error Message */}
        {error && (
          <div className="flex items-center p-3 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg">
            <AlertCircle size={18} className="text-red-600 dark:text-red-400 mr-2 flex-shrink-0" />
            <p className="text-sm text-red-800 dark:text-red-200">{error}</p>
          </div>
        )}

        {/* Success Message */}
        {success && (
          <div className="flex items-center p-3 bg-green-50 dark:bg-green-900/20 border border-green-200 dark:border-green-800 rounded-lg">
            <CheckCircle size={18} className="text-green-600 dark:text-green-400 mr-2 flex-shrink-0" />
            <p className="text-sm text-green-800 dark:text-green-200">{success}</p>
          </div>
        )}
      </div>
    </div>
  );
};

export default GrantCreditsSection; 